// @flow

import * as React from 'react';
import { isNull } from 'lodash/fp';

import ProgressiveRenderList from 'app/components/commons/ProgressiveRenderList';
import { percentageBetween } from 'app/utils/mathUtils';
import LiveEntityRow from './LiveEntityRow';
import { isChallengeComplete } from '../../utils/challengeUtils';
import {
  getFactoredEntityPositionSalary,
  getEntityPositionSalaryFactor
} from '../../selectors/salaryExpenditureSelector';

type Props = {
  displayWidget?: boolean,
  isMobile?: boolean,
  isRacing?: boolean,
  playerBoardTableInfo: Object,
  players?: Array<Object>,
  positionFilter: string | null,
  onWidgetClick: string => void,
  showProjectedScore: boolean,
  widgetPlayerId?: string
};

class PlayerBoardTable extends React.PureComponent<Props> {
  props: Props;

  static defaultProps = {
    displayWidget: false,
    isMobile: false,
    isRacing: false,
    players: [],
    widgetPlayerId: undefined
  };

  renderItem = (index: number) => {
    const {
      displayWidget,
      onWidgetClick,
      isRacing,
      playerBoardTableInfo,
      players = PlayerBoardTable.defaultProps.players,
      positionFilter,
      showProjectedScore,
      widgetPlayerId,
      isMobile
    } = this.props;

    const player = players[index];

    const {
      place,
      placeProjected,
      popularity,
      positions,
      salary,
      score,
      weight,
      win,
      winProjected
    } = player;

    const valueForMoney = percentageBetween(
      player.valueForMoney,
      player.minValueForMoney,
      player.maxValueForMoney
    );

    const salaryFactor = getEntityPositionSalaryFactor(positions, positionFilter);
    const factoredScore = score * salaryFactor;
    const factoredSalary = getFactoredEntityPositionSalary(salary, positions, positionFilter);

    return (
      <LiveEntityRow
        key={player.idStr}
        awayTeam={player.awayAbbr}
        compressedWidget={isMobile}
        gameOrder={player.gameOrder}
        gamePlaced={player.gamePlaced}
        gameStage={player.gameStage}
        homeTeam={player.homeAbbr}
        id={player.id}
        idStr={player.idStr}
        image={player.imageURI}
        initials={player.initials}
        isHome={player.isHome}
        isTeam={player.isTeam}
        isRacing={isRacing}
        isScratched={player.isScratched}
        labels={player.labels}
        name={player.name}
        number={player.number}
        onWidgetClick={onWidgetClick}
        playerBoardTableInfo={playerBoardTableInfo}
        position={player.positionStr}
        positionFilter={positionFilter}
        progress={player.progress}
        rank={player.rank}
        reverseOrder={player.reverseOrder}
        rider={player.rider}
        riderImage={player.riderImage}
        selected={player.selected}
        showProjectedScore={showProjectedScore}
        showWidget={displayWidget && player.idStr === widgetPlayerId}
        statusId={player.statusId}
        tableData={{
          place:
            (!showProjectedScore || isChallengeComplete(player.statusId)) && !isNull(place)
              ? place
              : placeProjected,
          popularity,
          salary: factoredSalary,
          score: factoredScore,
          valueForMoney,
          weight,
          win:
            (!showProjectedScore || isChallengeComplete(player.statusId)) && !isNull(win)
              ? win
              : winProjected
        }}
        trainer={player.trainer}
        win={win}
        place={place}
      />
    );
  };

  render() {
    const {
      players = PlayerBoardTable.defaultProps.players,
      showProjectedScore,
      widgetPlayerId
    } = this.props;

    return (
      <ProgressiveRenderList
        itemRenderer={this.renderItem}
        length={players.length}
        players={players} // force PureComponent update
        widgetPlayerId={widgetPlayerId} // force PureComponent update
        showProjectedScore={showProjectedScore}
      />
    );
  }
}

export default PlayerBoardTable;
